<script setup lang="ts">
import type { TooltipContentEmits, TooltipContentProps } from "reka-ui";
import type { HTMLAttributes } from "vue";
import { reactiveOmit } from "@vueuse/core";
import { TooltipArrow, TooltipContent, TooltipPortal, useForwardPropsEmits } from "reka-ui";
import { layerRoot } from "@/lib/layerRoot";
import { cn } from "@/lib/utils";

defineOptions({
  inheritAttrs: false,
});

// Edited from shadcn-vue's new-york-v4 TooltipContent: the panel takes the
// popover's surface (the one look every floating panel here shares) rather
// than an inverted chip, the arrow is opt-in, and it keeps 8px clear of the
// trigger and the window edge.
const props = withDefaults(defineProps<TooltipContentProps & { class?: HTMLAttributes["class"]; arrow?: boolean }>(), {
  sideOffset: 8,
  collisionPadding: 8,
  arrow: false,
});

const emits = defineEmits<TooltipContentEmits>();

const delegatedProps = reactiveOmit(props, "class", "arrow");
const forwarded = useForwardPropsEmits(delegatedProps, emits);
</script>

<!-- Edited from shadcn: wrap-anywhere, so a long unbroken word (a group name)
     breaks inside the panel instead of running out of it. -->
<template>
  <TooltipPortal :to="layerRoot()">
    <TooltipContent
      data-slot="tooltip-content"
      v-bind="{ ...forwarded, ...$attrs }"
      :class="
        cn(
          'z-50 w-fit animate-in rounded-md border bg-popover px-3 py-1.5 text-xs wrap-anywhere text-popover-foreground shadow-md fade-in-0 zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
          props.class,
        )
      "
    >
      <slot />

      <TooltipArrow
        v-if="arrow"
        class="z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-xs fill-popover"
      />
    </TooltipContent>
  </TooltipPortal>
</template>
